import React, { useEffect, useState, useCallback } from 'react'
import { FormattedMessage } from 'react-intl'
import { useParams } from 'react-router-dom'

import Container from 'components/Container'
import Row from 'components/Row'
import Col from 'components/Col'
import Accordion from 'components/Accordion'
import { siteThreatsTiming } from 'constants/siteThreatsTiming'
import Modal from '../component/Modal'
import AddTriggerEvaluation from './AddTriggerEvaluation'
import AddDetailsEffectTab from '../PressureModal/AddDetailsEffectTab'
import { AddNewThreatsButton } from './style'
import ThreatsContent from './ThreatsContent'
import ThreatsContentHeader from './ThreatsContentHeader'
import RemoveThreatModal from './RemoveThreatModal'
import {
  requestGetPressureThreats,
  requestCreateMonitoringThreat,
  requestUpdateMonitoringThreat,
  requestDeleteMonitoringThreat,
  requestLevelThreats,
  requestUpdateMonitoringThreatTriggerTimming,
  requestMarkPressureReviewed
} from 'api/monitoring'
import Loader from 'components/Loader'
import { setCopyPayload, setActiveDetail, clearActivePastes } from '../PressureModal/ecosystemCopyStore'
import ReviewPressureModal from './ReviewPressureModal'

// level threat option lists are populated from API at runtime via `requestLevelThreats`.
// Placeholders kept empty until API response populates them.

export const formattedTimingOptions = siteThreatsTiming.map((item) => {
  return {
    value: item.id,
    label: item.name
  }
})

export const parsePressureThreatsResponse = (payload) => {
  if (Array.isArray(payload)) {
    return { isReEvaluated: false, threats: payload }
  }
  if (payload && Array.isArray(payload.data)) {
    return { isReEvaluated: payload.isReEvaluated === true, threats: payload.data }
  }
  return { isReEvaluated: payload?.isReEvaluated === true, threats: [] }
}

const normalizeThreatItem = (item) => ({
  ...item,
  Level1ThreatId: item.level1_threat_id ?? item.Level1ThreatId ?? null,
  Level2ThreatId: item.level2_threat_id ?? item.Level2ThreatId ?? null,
  Level3ThreatId: item.level3_threat_id ?? item.Level3ThreatId ?? null,
  SiteThreatTimingId: item.site_threat_timing_id ?? item.SiteThreatTimingId ?? 3,
  createdAt: item.createdAt ?? item.created_at ?? null,
  threat_trigger_exists: item.threat_trigger_exists ?? item.threatTriggerExists ?? false,
  isReviewed: item.is_reviewed ?? item.isReviewed ?? true,
  previouslyAddedPressure: item.previously_added_pressure ?? item.previouslyAddedPressure ?? false
})

const sortThreatsByCreatedAt = (threatsArray) =>
  threatsArray.sort((a, b) => {
    const ta = a.createdAt ? Date.parse(a.createdAt) : 0
    const tb = b.createdAt ? Date.parse(b.createdAt) : 0
    return ta - tb
  })

export const defaultThreatsOptions = {
  level1Threats: [],
  level2Threats: [],
  level3Threats: [],
  siteThreatsTiming: formattedTimingOptions
}

const defaultThreat = {
  name: 'default',
  Level1ThreatId: null,
  Level2ThreatId: null,
  Level3ThreatId: null,
  siteThreatsTiming: null,
  id: ''
}

const filterAvailableThreatsOptions = (threatsList, baseOptions = defaultThreatsOptions) => {
  const lvl3 = baseOptions.level3Threats.filter((element) => {
    return !threatsList.find((selected) => {
      return selected.Level3ThreatId === element.value
    })
  })

  const lvl2 = baseOptions.level2Threats.filter((element) => {
    return (
      lvl3.some((lvl3Threads) => {
        return lvl3Threads.Level2ThreatId === element.value
      }) ||
      !threatsList.find((selected) => {
        return selected.Level2ThreatId === element.value
      })
    )
  })

  const lvl1 = baseOptions.level1Threats.filter((element) => {
    return (
      lvl2.some((lvl2Threads) => {
        return lvl2Threads.Level1ThreatId === element.value
      }) ||
      !threatsList.find((selected) => {
        return selected.Level1ThreatId === element.value
      })
    )
  })

  return { level1Threats: lvl1, level2Threats: lvl2, level3Threats: lvl3 }
}

const Threats = ({
  site,
  pressure,
  disabled,
  showTriggerEvaluation: propShowTriggerEvaluation,
  setShowTriggerEvaluation: propSetShowTriggerEvaluation,
  initialSelectedPressure = null,
  initialModalInfo = {}
  , initialOpenTab = null,
  isEditable,
  onBackToPressure,
  checkPressureThreatsCompleted = () => {},
  refreshThreats = 0
}) => {
  const [localShowTriggerEvaluation, setLocalShowTriggerEvaluation] = useState(false)
  const showTriggerEvaluation =
    typeof propShowTriggerEvaluation !== 'undefined' ? propShowTriggerEvaluation : localShowTriggerEvaluation
  const setShowTriggerEvaluation =
    typeof propSetShowTriggerEvaluation !== 'undefined' ? propSetShowTriggerEvaluation : setLocalShowTriggerEvaluation
  const [threatsList, setThreatsList] = useState([])
  const [selectedPressure, setSelectedPressure] = useState(null)
  const lastSelectedTimingRef = React.useRef(null)
  const [threatForDelete, setThreatForDelete] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [initialOpenIndex, setInitialOpenIndex] = useState(null)
  const [threatOptions, setThreatOptions] = useState(defaultThreatsOptions)
  const [checkedBlankThreat, setCheckedBlankThreat] = useState(false)
  const [isPressureModalOpen, setIsPressureModalOpen] = useState(false);
  const [activeDetailsSubTab, setActiveDetailsSubTab] = useState(initialOpenTab === 'ecosystems' ? 'ecosystems' : 'species')
  const [isReEvaluated, setIsReEvaluated] = useState(false)
  const [reviewModal, setReviewModal] = useState(null)
  const params = useParams()
  // derive available options based on fetched threat options
  const options = filterAvailableThreatsOptions(threatsList, threatOptions)

  useEffect(() => {
    checkPressureThreatsCompleted(checkedBlankThreat);
    setCopyPayload(null)
    try { setActiveDetail(null) } catch (e) {}
    try { clearActivePastes() } catch (e) {}
  }, [])

  // Keep the timing value on the main threats list while a pressure is selected
  useEffect(() => {
    if (!selectedPressure) return
    const threatId = selectedPressure?.threatItemId || selectedPressure?.id
    if (!threatId) return
    const timing =
      selectedPressure?.SiteThreatTimingId ??
      selectedPressure?.siteThreatTimingId ??
      selectedPressure?.site_threat_timing_id ??
      null
    // remember last timing so it can be restored if UI flow hides it temporarily
    lastSelectedTimingRef.current = timing
    if (timing === null) return
    setThreatsList((prev) =>
      prev.map((t) =>
        String(t.id) === String(threatId) ? { ...t, SiteThreatTimingId: t.SiteThreatTimingId ?? timing } : t
      )
    )
  }, [selectedPressure])

  // When returning from the modal (showTriggerEvaluation toggles false), restore timing
  useEffect(() => {
    if (showTriggerEvaluation) return
    const storedTiming = lastSelectedTimingRef.current
    if (storedTiming == null) return
    const threatId = selectedPressure?.threatItemId || selectedPressure?.id
    if (!threatId) return
    setThreatsList((prev) =>
      prev.map((t) => (String(t.id) === String(threatId) ? { ...t, SiteThreatTimingId: storedTiming } : t))
    )
  }, [showTriggerEvaluation, selectedPressure])

  const applyThreatsPayload = (payload) => {
    const parsed = parsePressureThreatsResponse(payload)
    setIsReEvaluated(parsed.isReEvaluated)
    const threatsArray = parsed.threats.map(normalizeThreatItem)
    setThreatsList(sortThreatsByCreatedAt(threatsArray))
    setInitialOpenIndex(null)
    setIsLoading(false)
  }

  const getThreats = useCallback(() => {
    setIsLoading(true)
    const pressureId = pressure
    const siteId = params?.id
    requestGetPressureThreats(siteId, pressureId)
      .then((data) => {
        const parsed = parsePressureThreatsResponse(data)
        if (parsed.threats.length === 0) {
          if (!siteId) {
            setIsReEvaluated(parsed.isReEvaluated)
            setThreatsList([])
            setInitialOpenIndex(null)
            setIsLoading(false)
            return
          }
          requestGetPressureThreats(siteId, pressureId)
            .then((monitorData) => {
              applyThreatsPayload(monitorData)
            })
            .catch((err) => {
              console.error('[Threats] requestGetMonitoringSiteThreats failed', err)
              setThreatsList([])
              setInitialOpenIndex(null)
              setIsLoading(false)
            })
        } else {
          applyThreatsPayload(data)
        }
      })
      .catch((err) => {
        console.error('[Threats] requestGetPressureThreats failed', err)
        setThreatsList([])
        setInitialOpenIndex(null)
        setIsLoading(false)
      })
  })

  const updateThreats = (threatId, updatedThreats) => {
    if (!updatedThreats || !threatId) {
      return
    }
    requestUpdateMonitoringThreat(pressure, threatId, updatedThreats).then((data) => {
      if(data) {
        requestUpdateMonitoringThreatTriggerTimming(threatId, { site_threat_timing_id: updatedThreats.SiteThreatTimingId })
      }
      // If only timing changed, update local list in-place so the
      // item's expansion state and position remain unchanged.
      const keys = Object.keys(updatedThreats || {})
      if (keys.length === 1 && Object.prototype.hasOwnProperty.call(updatedThreats, 'SiteThreatTimingId')) {
        setThreatsList((prev) =>
          prev.map((t) => (t.id === threatId ? { ...t, SiteThreatTimingId: updatedThreats.SiteThreatTimingId } : t))
        )
      } else {
        // For other changes, refresh from server (may affect ordering)
        getThreats()
      }
    })
  }

  const createThreats = (newThreats) => {
    return requestCreateMonitoringThreat(pressure, newThreats)
      .then((data) => {
        const responseData = {
          ...data,
          Level1ThreatId: data.level1_threat_id ?? data.Level1ThreatId ?? null,
          Level2ThreatId: data.level2_threat_id ?? data.Level2ThreatId ?? null,
          Level3ThreatId: data.level3_threat_id ?? data.Level3ThreatId ?? null,
          SiteThreatTimingId: data.site_threat_timing_id ?? data.SiteThreatTimingId ?? null,
          createdAt: data.createdAt ?? data.created_at ?? null,
          id: data.id ?? ''
        }
        // If there is a placeholder/default threat (id === ''), replace it with the created record
        setThreatsList((prev) => {
          const hasPlaceholder = prev.some((t) => t.id === '')
          if (hasPlaceholder) {
            return prev.map((t) => (t.id === '' ? responseData : t))
          }
          // otherwise append the created record
          return [...prev, responseData]
        })
        return responseData
      })
      .catch((err) => {
        console.error('[Threats] createMonitoringThreat failed', err)
        throw err
      })
  }

  const handleOpenRemoveModal = (threat) => {
    if (threat.id === '') {
      return getThreats()
    }
    setThreatForDelete(threat)
  }

  const markPressureReviewed = async (threatId) => {
    if (!threatId) return
    await requestMarkPressureReviewed(threatId)
  }

  const handleOpenReviewModal = (payload) => {
    setReviewModal(payload)
  }

  const handleCloseReviewModal = () => {
    setReviewModal(null)
  }

  const handleReviewedFromModal = async () => {
    try {
      const threatId = reviewModal?.threatItemId || reviewModal?.item?.id
      await markPressureReviewed(threatId)
      setReviewModal(null)
      getThreats()
    } catch (err) {
      console.error('[Threats] mark pressure reviewed failed', err)
    }
  }

  const handleEditFromModal = async () => {
    try {
      const threatId = reviewModal?.threatItemId || reviewModal?.item?.id
      const displayName = reviewModal?.displayName
      const data = reviewModal?.data
      await markPressureReviewed(threatId)
      setReviewModal(null)
      setSelectedPressure({
        data: Array.isArray(data) ? data : data || [],
        threatItemId: threatId,
        displayName
      })
      setShowTriggerEvaluation(true)
    } catch (err) {
      console.error('[Threats] mark pressure reviewed before edit failed', err)
    }
  }

  const deleteThreats = (id) => {
    if (id === '') {
      return
    }
    requestDeleteMonitoringThreat(id).then((data) => {
      getThreats()
    })
  }

  const handleAddThreats = () => {
    const availableToAddNewThreats = threatsList.every((element) => {
      return element.id !== ''
    })

    if (!availableToAddNewThreats) {
      return
    }

    checkPressureThreatsCompleted(true)
    setCheckedBlankThreat(true);
    setThreatsList((s) => {
      setInitialOpenIndex(s.length)
      return [...s, defaultThreat]
    })
  }

  useEffect(() => {
    if (!pressure) return
      getThreats()
  }, [pressure, refreshThreats])

  useEffect(() => {
    if (!showTriggerEvaluation && pressure) {
      getThreats()
    }
  }, [showTriggerEvaluation])

  useEffect(() => {
    // getThreats()
    // fetch level threats from API and map into select options
    requestLevelThreats()
      .then((data) => {
        const raw = Array.isArray(data) ? data : []
        const lvl1 = raw.map((l1) => ({ value: l1.id, label: l1.name }))
        const lvl2 = []
        const lvl3 = []
        raw.forEach((l1) => {

          if (Array.isArray(l1.Level2Threats)) {
            l1.Level2Threats.forEach((l2) => {
              lvl2.push({ value: l2.id, label: l2.name, Level1ThreatId: l1.id, title: l2.title })
              if (Array.isArray(l2.Level3Threats)) {
                l2.Level3Threats.forEach((l3) => {
                  lvl3.push({ value: l3.id, label: l3.name, title: l3.title, Level2ThreatId: l2.id, Level1ThreatId: l1.id })
                })
              }
            })
          }
        })
        setThreatOptions({ level1Threats: lvl1, level2Threats: lvl2, level3Threats: lvl3, siteThreatsTiming: formattedTimingOptions })
      })
      .catch((err) => {
        console.error('[Threats] requestLevelThreats failed', err)
        setThreatOptions(defaultThreatsOptions)
      })
  }, [])

  // if parent provided an initial selected payload (via navigation state), open details inline
  useEffect(() => {
    if (initialSelectedPressure) {
      setSelectedPressure(initialSelectedPressure)
      setShowTriggerEvaluation(true)
    }
  }, [initialSelectedPressure])

  useEffect(() => {
    setActiveDetailsSubTab(initialOpenTab === 'ecosystems' ? 'ecosystems' : 'species')
  }, [initialOpenTab])

  useEffect(() => {
    if (!showTriggerEvaluation) {
      document.body.style.overflow = 'auto'
      document.body.classList.remove('ReactModal__Body--open')
      document.body.classList.remove('modal-open')
    }
  }, [showTriggerEvaluation])

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <Loader />
      </div>
    )
  }

  return (
    <>
      {showTriggerEvaluation ? (
        (selectedPressure && Array.isArray(selectedPressure.data) && selectedPressure.data.length > 0) ||
        (Array.isArray(selectedPressure) && selectedPressure.length > 0) ? (
          <AddDetailsEffectTab
            preselectedItems={selectedPressure}
            setIncomingSelection={() => {}}
            setMainTab={() => setShowTriggerEvaluation(false)}
            setEcosystemsCount={() => {}}
            threatItemId={selectedPressure?.threatItemId || selectedPressure?.id}
            selectedSubTab={setActiveDetailsSubTab}
            parentTabSelected={activeDetailsSubTab}
            isEditable={isEditable}
            onBackToPressure={onBackToPressure}
          />
        ) : (
          <AddTriggerEvaluation item={selectedPressure} onBack={() => setShowTriggerEvaluation(false)} />
        )
      ) : (
        <Container>
          <Row>
            <Col md={9} mdOffset={2}>
              {threatsList.length === 0 ? (
                <ThreatsContent
                  item={defaultThreat}
                  options={options}
                  createThreats={createThreats}
                  defaultThreatsOptions={threatOptions}
                  updateThreats={updateThreats}
                  disabled={disabled}
                  setCheckedBlankThreat={checkPressureThreatsCompleted}
                  isEditable={isEditable}
                />
              ) : (
                <Accordion
                  key={`threats-accordion-${initialOpenIndex ?? 'none'}-${threatsList.length}`}
                  initialActive={initialOpenIndex}
                  contentComponent={ThreatsContent}
                  headerComponent={ThreatsContentHeader}
                  headerProps={{
                    handleOpenRemoveModal,
                    disabled,
                    showTriggerEvaluation,
                    setShowTriggerEvaluation,
                    setSelectedPressure,
                    threatOptions,
                    initialOpenModalForThreatId: initialModalInfo?.modalThreatId,
                    initialSelectedPressure,
                    setIsLoading,
                    setCheckedBlankThreat: checkPressureThreatsCompleted,
                    isEditable,
                    onBackToPressure,
                    setIsModalOpen: setIsPressureModalOpen,
                    isReEvaluated,
                    onOpenReviewModal: handleOpenReviewModal
                  }}
                  contentProps={{ options, createThreats, updateThreats, defaultThreatsOptions: threatOptions, site, disabled, setCheckedBlankThreat: checkPressureThreatsCompleted, isEditable }}
                  items={threatsList}
                />
              )}
            </Col>
          </Row>
          {isEditable && (
          <Row>
            <Col md={9} mdOffset={2}>
              {!showTriggerEvaluation && (
                <AddNewThreatsButton onClick={handleAddThreats} disabled={!isEditable} style={{ cursor: 'pointer' }}>
                  <FormattedMessage id="newThreat" />
                </AddNewThreatsButton>
              )}
            </Col>
          </Row>
          )}
          <Modal
            width={'416px'}
            isOpen={Boolean(threatForDelete.id)}
            toggleModal={() => {
              setThreatForDelete({})
            }}
            modalTitleId="removeThreat"
            bottomBorder={true}
            sideModal={false}
          >
            <RemoveThreatModal
              item={threatForDelete}
              threatsList={threatOptions}
              closeModal={() => {
                setThreatForDelete({})
              }}
              confirmRemove={() => {
                deleteThreats(threatForDelete.id)
                setThreatForDelete({})
              }}
            />
          </Modal>
          <ReviewPressureModal
            isOpen={Boolean(reviewModal)}
            title={reviewModal?.displayName}
            preselectedItems={reviewModal}
            threatItemId={reviewModal?.threatItemId}
            onBack={handleCloseReviewModal}
            onReviewed={handleReviewedFromModal}
            onEdit={handleEditFromModal}
          />
        </Container>
      )}
    </>
  )
}

export default Threats
